import {finalize} from 'rxjs/operators';
import { Component, OnInit, Input } from '@angular/core';
import { Router } from '@angular/router';
import { NotifierService, DialogService } from '../../ui';
import { DLCompany } from './company';
import { DLAuthorization } from './authorization';
import { sortBy } from 'lodash';
import { CLAccount } from '../../shares-ledger/client';
import { DLAuthorizationFilter, DLAuthorizationService } from './authorization.service';
import { CLAccountSearchDialogComponent } from '../../shares-ledger/client/account-search-dialog.component';
import { DLCompanySearchDialogComponent } from './company-search-dialog.component';

@Component({
  selector: 'dl-authorization-list',
  templateUrl: './authorization-list.component.html',
})
export class DLAuthorizationListComponent implements OnInit {

  @Input()
  account: CLAccount

  @Input()
  company: DLCompany

  @Input()
  title: string = ''

  filter: DLAuthorizationFilter;

  authorizations: DLAuthorization[]

  constructor(
    private authorizationService: DLAuthorizationService,
    private router: Router,
    private notifier: NotifierService,
    private dialogService: DialogService
  ) { }

  ngOnInit() {
    this.filter = new DLAuthorizationFilter();

    if (this.account) {
      this.filter.account = this.account.id;
    }

    if (this.company) {
      this.filter.company = this.company.id;
    }
    
    // Hide progress bar on first load
    this.fetch(false);
  }

  private fetch(showNotification = true) {
    if (showNotification) {
      this.notifier.startProgress();
    }

    this.authorizationService
      .authorizations(this.filter).pipe(
      finalize(() => {
        if (showNotification) {
          this.notifier.endProgress();
        }
      }))
      .subscribe((result) => {
        if (this.account) {
          this.authorizations = sortBy(result, a => a.company.name);
        }
        else if (this.company) {
          this.authorizations = sortBy(result, a => a.account.name);
        }
      });
  }

  showCLAccount(account: CLAccount) {
    if (account.deleted) {
      this.router.navigate(['shares-ledger', 'client-accounts', 'deleted', account.id]);
    }
    else {
      this.router.navigate(['shares-ledger', 'client-accounts', account.id]);
    }
  }

  showDLCompany(company: DLCompany) {
    this.router.navigate(['documents-ledger', 'companies', company.id]);
  }

  showItem(authorization: DLAuthorization, event: Event) {
    let nodeName = (<Element>event.target).nodeName;

    if (nodeName === 'TD' || nodeName === 'TR') {
      if (this.account) {
        this.showDLCompany(authorization.company);
      }
      else if (this.company) {
        this.showCLAccount(authorization.account);
      }
    }
  }

  add(event: Event) {

    // We have an company
    if (this.company) {
      this.dialogService.show({
        component: CLAccountSearchDialogComponent,
        props: {
          onClose: () => {
            this.dialogService.hide();
          },
  
          onSelect: (account: CLAccount) => {
            this.dialogService.hide();
  
            this.save(account, this.company);
          }
        }
      });
    }
    // We have an account
    else if (this.account) {
      this.dialogService.show({
        component: DLCompanySearchDialogComponent,
        props: {
          onClose: () => {
            this.dialogService.hide();
          },
  
          onSelect: (company: DLCompany) => {
            this.dialogService.hide();
  
            this.save(this.account, company);
          }
        }
      });
    }

    event.preventDefault();
  }

  save(account: CLAccount, company: DLCompany) {
    const authorization = new DLAuthorization({
      account,
      company,
      type: 'FULL',
    });

    if (!authorization.valid) {
      return;
    }

    let notifier = this.notifier;

    notifier.updateProgress(20);

    function onComplete() {
      notifier.endProgress();
    }

    this.authorizationService
      .create(authorization).pipe(
      finalize(onComplete))
      .subscribe((authorization) => {
        this.notifier.success('Authorization ajoutée avec succès');

        this.fetch();
      }, (err) => {
        this.notifier.endProgress();
      });
  }

  delete(authorization: DLAuthorization) {
    this.dialogService
      .confirmDelete('Voulez-vous supprimer cette autorisation?')
      .subscribe((result) => {
        if (!result) {
          return;
        }

        this.notifier.startProgress();

        this.authorizationService
          .delete(authorization.id)
          .subscribe((id) => {
            this.notifier.success('Autorisation supprimée avec succès');

            this.fetch();
          }, (err) => {
            this.notifier.endProgress();
          });
      });
    
    return false;
  }
}
